import { useEffect, useState } from 'react';
import { format, startOfMonth } from 'date-fns';
import supervisorService from '../../services/supervisorService';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

export default function TeamKPIs() {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [periodType, setPeriodType] = useState('monthly');
  const [periodStart, setPeriodStart] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));

  useEffect(() => {
    fetchTeamKPIs();
  }, [periodType, periodStart]);

  const fetchTeamKPIs = async () => {
    try {
      setLoading(true);
      const response = await supervisorService.getTeamKPIs({
        period_type: periodType,
        period_start: periodStart,
      });
      setKpis(response.data?.kpis || []);
      setTeamStats(response.data?.teamStats || null);
    } catch (error) {
      console.error('Failed to fetch team KPIs:', error);
      toast.error('Failed to fetch team KPIs');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    return parseFloat(num).toFixed(decimals);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Team KPIs</h1>
        <div className="flex items-center space-x-4">
          <select
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <input
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            className="px-3 py-2 border rounded-md"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          {/* Team Statistics */}
          {teamStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-sm text-gray-600">Avg Distance</div>
                <div className="text-3xl font-bold text-primary-600">
                  {formatNumber(teamStats.avgDistance, 0)} km
                </div>
                <div className="text-xs text-gray-500 mt-1">per driver</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-sm text-gray-600">Avg Trips</div>
                <div className="text-3xl font-bold text-green-600">
                  {formatNumber(teamStats.avgTrips, 0)}
                </div>
                <div className="text-xs text-gray-500 mt-1">per driver</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-sm text-gray-600">Avg Fuel Efficiency</div>
                <div className="text-3xl font-bold text-blue-600">
                  {formatNumber(teamStats.avgFuelEfficiency)} km/L
                </div>
                <div className="text-xs text-gray-500 mt-1">team average</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-sm text-gray-600">Avg Score</div>
                <div className="text-3xl font-bold text-yellow-600">
                  {formatNumber(teamStats.avgOverallScore, 1)}
                </div>
                <div className="text-xs text-gray-500 mt-1">out of 100</div>
              </div>
            </div>
          )}

          {/* Driver KPIs Table */}
          {kpis.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No KPI data available</h3>
              <p className="mt-2 text-gray-600">
                There are no KPI metrics for the selected period.
              </p>
              <div className="mt-6 text-left max-w-md mx-auto bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Troubleshooting Steps:</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-yellow-800">
                  <li>Check that the <strong>period dates match</strong> what you calculated
                    <div className="ml-6 mt-1 text-xs">
                      Currently viewing: <strong>{periodType}</strong> starting <strong>{periodStart}</strong>
                    </div>
                  </li>
                  <li>Ensure drivers have <strong>submitted log entries</strong> for this period</li>
                  <li>Verify log entries have been <strong>approved</strong> by supervisors</li>
                  <li>Go to Admin Dashboard and <strong>calculate KPIs</strong> for the same period</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Individual Driver Performance</h2>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Driver
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trips
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Distance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fuel Eff.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {kpis.map((kpi) => (
                    <tr key={kpi.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white text-sm">
                            {kpi.driver?.user?.first_name?.[0]?.toUpperCase() || 'D'}
                          </div>
                          <div className="ml-3">
                            <div className="font-medium text-gray-900">
                              {kpi.driver?.user?.first_name} {kpi.driver?.user?.last_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {kpi.total_trips || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {formatNumber(kpi.total_distance, 0)} km
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {formatNumber(kpi.total_hours, 1)} hrs
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {formatNumber(kpi.fuel_efficiency)} km/L
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-gray-900 font-medium">
                            {formatNumber(kpi.overall_score, 1)}
                          </span>
                          <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary-600 rounded-full h-2"
                              style={{ width: `${Math.min((kpi.overall_score || 0), 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* KPI Explanation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">About Team KPIs</h3>
            <div className="space-y-2 text-sm text-blue-800">
              <p>
                <strong>Team KPIs</strong> provide insights into your team's performance by analyzing approved log entries.
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Trips:</strong> Total number of trips completed</li>
                <li><strong>Distance:</strong> Total kilometers driven</li>
                <li><strong>Hours:</strong> Total hours worked (based on shift times)</li>
                <li><strong>Fuel Efficiency:</strong> Kilometers per liter (distance ÷ fuel used)</li>
                <li><strong>Score:</strong> Overall performance score (calculated from various metrics)</li>
              </ul>
              <p className="mt-3">
                KPIs are automatically calculated from approved log entries. If you don't see data, ensure that:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Drivers have submitted log entries for the selected period</li>
                <li>Log entries have been approved by supervisors</li>
                <li>KPI calculations have been triggered (contact admin if needed)</li>
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
